import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { CloseIcon } from './icons'

interface ModalProps {
  open: boolean
  title: string
  subtitle?: ReactNode
  onClose: () => void
  children: ReactNode
  /** Широкий лист — для форм/сеток (настройки, подключения). */
  wide?: boolean
}

/**
 * Универсальный модальный «лист»: шапка (заголовок + крестик) и прокручиваемое тело
 * под произвольный контент (в отличие от {@link ConfirmDialog} — фиксированного da/нет).
 * Рендерится в портал; закрывается по Escape и клику по подложке; пока открыт — блокирует
 * прокрутку фона. Доступно: role=dialog + aria-modal + автофокус крестика.
 */
export function Modal({ open, title, subtitle, onClose, children, wide = false }: ModalProps) {
  const closeRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    closeRef.current?.focus()
    // Блокируем прокрутку фона, пока попап открыт (возвращаем прежнее значение при закрытии).
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [open, onClose])

  if (!open) return null

  return createPortal(
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div
        className={`modal modal--sheet${wide ? ' modal--wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal__bar">
          <div>
            <div className="modal__heading">{title}</div>
            {subtitle ? <div className="modal__sub">{subtitle}</div> : null}
          </div>
          <button
            type="button"
            ref={closeRef}
            className="icon-btn"
            onClick={onClose}
            aria-label="Закрыть"
          >
            <CloseIcon size={18} />
          </button>
        </div>
        <div className="modal__scroll">{children}</div>
      </div>
    </div>,
    document.body,
  )
}
